import React from "react";
import { Check, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Activity } from "../../types/tasks";
import { ACTIVITY_TYPE_ICON } from "../../constants/taskConstants";

interface Props {
  activity: Activity;
  /** True when this activity was already completed in the DB when the form opened.
   *  Prevents the user from unchecking a persisted completion. */
  locked?: boolean;
  readonly?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleComplete: () => void;
}

const dueTone = (a: Activity): string => {
  if (a.completedAt) return "text-gray-400 line-through";
  if (!a.dueDate) return "text-gray-500";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(a.dueDate);
  due.setHours(0, 0, 0, 0);
  if (due.getTime() < today.getTime()) return "text-red-600 font-medium";
  if (due.getTime() === today.getTime()) return "text-blue-600 font-medium";
  return "text-gray-500";
};

const formatDue = (a: Activity, t: (k: string) => string): string => {
  if (!a.dueDate) return t("list.noDueDate");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(a.dueDate);
  const dueDay = new Date(due);
  dueDay.setHours(0, 0, 0, 0);
  const base =
    dueDay.getTime() < today.getTime()
      ? t("list.overdue")
      : dueDay.getTime() === today.getTime()
        ? t("list.today")
        : due.toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          });
  return a.dueTime ? `${base} · ${a.dueTime}` : base;
};

export const ActivityCard: React.FC<Props> = ({
  activity,
  locked = false,
  readonly = false,
  onEdit,
  onDelete,
  onToggleComplete,
}) => {
  const { t } = useTranslation("tasks");
  const Icon = ACTIVITY_TYPE_ICON[activity.type];
  const isDone = !!activity.completedAt;

  const effectiveLocked = locked || readonly;

  const checkboxClass = [
    "mt-0.5 w-5 h-5 rounded border flex items-center justify-center shrink-0 transition-colors",
    isDone
      ? "bg-green-500 border-green-500 text-white"
      : "border-gray-300",
    effectiveLocked
      ? "cursor-default opacity-80"
      : isDone
        ? "cursor-pointer hover:bg-green-600"
        : "cursor-pointer hover:border-primary",
  ].join(" ");

  const checkboxTitle = effectiveLocked
    ? t("activity.completed")
    : isDone
      ? t("activity.markIncomplete")
      : t("activity.markComplete");

  return (
    <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:border-gray-300 transition-colors">
      <button
        type="button"
        onClick={effectiveLocked ? undefined : onToggleComplete}
        disabled={effectiveLocked}
        title={checkboxTitle}
        aria-label={checkboxTitle}
        className={checkboxClass}
      >
        {isDone && <Check className="w-3.5 h-3.5" />}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-gray-500 shrink-0" />
          <span
            className={`flex-1 min-w-0 truncate text-sm font-medium ${
              isDone ? "text-gray-400 line-through" : "text-dark-900"
            }`}
          >
            {activity.title}
          </span>
          <span className="shrink-0 whitespace-nowrap text-xs text-gray-400">
            · {t(`activity.types.${activity.type}`)}
          </span>
        </div>

        {activity.notes && (
          <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
            {activity.notes}
          </p>
        )}

        <div className="flex items-center gap-3 mt-1.5 text-xs">
          <span className={dueTone(activity)}>{formatDue(activity, t)}</span>
          {activity.reminder !== "none" && !isDone && (
            <span className="text-gray-400">
              · {t(`activity.reminderOptions.${activity.reminder}`)}
            </span>
          )}
        </div>

        {activity.result && (
          <div className="text-xs text-gray-500 mt-1.5 italic">
            {t("activity.result")}: {activity.result}
          </div>
        )}
      </div>

      {!readonly && !locked && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-dark-900 rounded transition-colors"
            title={t("activity.edit")}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
            title={t("form.delete")}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};
